import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { StateSeal } from '../../lib/StateMaterial'
import { refreshIdentity, useIdentity } from '../../lib/identity'
import { catalogExit } from '../../lib/catalogExit'
import { clearInviteToken } from '../../lib/inviteToken'
import { orgErrorCode, orgErrorMessage } from '../../lib/orgAccess'
import { orgApi } from '../../api/endpoints'

/**
 * InviteAcceptPanel — the screen with the most dead ends in the product, and
 * therefore the one where every branch is written out longhand.
 *
 * The server's contract is deliberately terse: acceptance is a transaction
 * asserting `status==='pending'`, not expired, and the invite email equals the
 * Auth email lowercased. Two tabs on one link → the second gets **409**. A
 * forwarded link accepted by the wrong person → **403**. Neither of those may
 * ever reach a human as a number.
 *
 * ── EVERY STATE, AND ITS EXIT ─────────────────────────────────────────────
 *   valid + signed out          → branded accept, sign in OR create an account
 *   valid + signed in (right)   → confirm; declining returns to the app
 *   signed in as the WRONG one  → names both addresses; sign in as the invited
 *                                 person, or ignore it and keep browsing
 *   already in THIS org         → "you're already on this team" → Team
 *   already in ANOTHER org      → the conflict, resolved BY ROLE — see below
 *   expired / revoked           → says WHICH, and offers to ask for a new one
 *   seat limit reached since    → tells the INVITEE it is not their fault
 *   no token / malformed        → the link is incomplete → sign in / browse
 *
 * Not one of those ends on a bare error code, and not one of them ends on a
 * screen whose only control is "sign out".
 *
 * ── THE EXITS KNOW WHICH SHELL THEY ARE IN ────────────────────────────────
 * This panel is rendered VERBATIM by `routes/OrgInvite.tsx` (desktop) and
 * `mobile/screens/account/InviteAcceptScreen.tsx` (app shell), and `/search`
 * exists on the desktop tree only. Every "browse" exit therefore comes from
 * `lib/catalogExit.ts`, which also drops the promise for a signed-out visitor
 * in the auth-first app shell instead of labelling a bounce to /welcome
 * "Browse the catalog".
 *
 * ── AND WHY `already_in_org` ASKS THE SERVER A SECOND QUESTION ────────────
 * It used to say "leave your current team first — you'll find it on your team
 * screen". For an org OWNER that instruction cannot be followed: the Team
 * screen draws no leave control on the owner's own row and
 * `seats.ts::mayRemoveMember` refuses it with `owner_cannot_be_removed`. So the
 * panel reads the caller's role from `GET /org` and branches: a member or admin
 * gets a real one-tap "leave and join" (the server has always allowed
 * self-removal), and an owner is told the truth — transfer ownership, or talk
 * to us — instead of being sent to look for a button that is not there.
 */

type Outcome =
  | { kind: 'idle' }
  | { kind: 'working' }
  | { kind: 'joined'; orgName: string | null; alreadyMember: boolean; needsPersonalPhone: boolean }
  | { kind: 'failed'; code: string | null; message: string }

/** What we know about the org the caller is ALREADY in, for `already_in_org`. */
type Conflict =
  | { kind: 'loading' }
  /** The read failed / the flag is off — we render the honest, role-free copy. */
  | { kind: 'unknown' }
  | { kind: 'known'; orgRole: 'owner' | 'admin' | 'member' | null; orgName: string }

export function InviteAcceptPanel({
  token,
  onSignIn,
  onSignUp,
}: {
  /** The raw token from `?invite=` / the stash. Empty ⇒ the incomplete-link state. */
  token: string | null
  /** Shell-specific navigation — desktop `/signin`, mobile `/signin`. */
  onSignIn: () => void
  onSignUp: () => void
}) {
  const navigate = useNavigate()
  const identity = useIdentity()
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' })
  const [conflict, setConflict] = useState<Conflict>({ kind: 'loading' })
  const [leaving, setLeaving] = useState(false)
  const [leaveError, setLeaveError] = useState<string | null>(null)
  // Optional personal number captured on the confirm screen — the invite token
  // is single-use, so this rides along WITH the accept call; there is no second
  // chance to attach it once the token is spent.
  const [phone, setPhone] = useState('')

  const exit = catalogExit(identity.signedIn)
  const browse = {
    label: exit.label,
    onClick: () => navigate(exit.path),
    testId: 'invite-browse',
  }

  // The token is single-use — a re-accept 409s — so `leaveAndJoin` reuses this
  // same closure and the phone it carries.
  const accept = useCallback(async () => {
    if (!token) return
    setOutcome({ kind: 'working' })
    try {
      const res = await orgApi.acceptInvite(token, phone.trim() || undefined)
      clearInviteToken()
      feedback.success()
      // The membership just changed and NOTHING in the token says so — org
      // identity resolves from Firestore by design. Force a token refresh so
      // the shell re-reads `/me` and the nav stops describing the old world.
      void refreshIdentity()
      setOutcome({
        kind: 'joined',
        orgName: res.orgName,
        alreadyMember: res.alreadyMember,
        needsPersonalPhone: res.needsPersonalPhone === true,
      })
    } catch (err) {
      feedback.deny()
      setOutcome({
        kind: 'failed',
        code: orgErrorCode(err),
        message: orgErrorMessage(err, 'We couldn’t use that invite.'),
      })
    }
  }, [token, phone])

  // Clear the stash once the token is spent OR permanently unusable, so a
  // later visit does not silently retry a link that will never work again.
  useEffect(() => {
    if (outcome.kind !== 'failed') return
    if (['invite_not_found', 'invite_revoked', 'invite_expired', 'invite_already_accepted'].includes(outcome.code ?? '')) {
      clearInviteToken()
    }
  }, [outcome])

  // `already_in_org` is the ONE refusal whose remedy depends on who you are in
  // the org you are already in. Ask, once, and degrade to role-free copy that
  // still has an exit if the answer never comes.
  useEffect(() => {
    if (outcome.kind !== 'failed' || outcome.code !== 'already_in_org') return
    let cancelled = false
    setConflict({ kind: 'loading' })
    orgApi.get()
      .then((res) => {
        if (cancelled) return
        setConflict({
          kind: 'known',
          orgRole: res.org.viewerOrgRole,
          orgName: res.org.displayName || res.org.legalName || 'your current company',
        })
      })
      .catch(() => { if (!cancelled) setConflict({ kind: 'unknown' }) })
    return () => { cancelled = true }
  }, [outcome])

  /** The non-owner remedy, end to end: leave, then spend the invite. */
  const leaveAndJoin = useCallback(async () => {
    if (!identity.uid) return
    setLeaving(true)
    setLeaveError(null)
    try {
      await orgApi.removeMember(identity.uid)
      feedback.dim()
      setOutcome({ kind: 'idle' })
      await accept()
    } catch (err) {
      feedback.deny()
      setLeaveError(orgErrorMessage(err, 'We couldn’t take you off that team.'))
    } finally {
      setLeaving(false)
    }
  }, [accept, identity.uid])

  /* ── no token ──────────────────────────────────────────────────────── */
  if (!token) {
    return (
      <Frame
        state="rejected"
        heading="That invite link is incomplete."
        body="Links can get cut in half by email clients. Open the original message and tap the button again, or ask whoever invited you to send a fresh one."
        actions={[
          { label: 'Sign in', onClick: onSignIn, primary: true, testId: 'invite-signin' },
          browse,
        ]}
        testId="invite-no-token"
      />
    )
  }

  /* ── still settling ────────────────────────────────────────────────── */
  if (identity.status === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[560px] px-4 py-10 md:px-6" data-testid="invite-loading">
        <div className="skeleton h-8 w-56 rounded" />
        <div className="skeleton mt-3 h-4 w-full rounded" />
        <div className="skeleton mt-2 h-4 w-3/4 rounded" />
        <div className="skeleton mt-6 h-11 w-44 rounded-full" />
        <span className="sr-only" role="status">Checking your invite</span>
      </div>
    )
  }

  /* ── signed out ────────────────────────────────────────────────────── */
  if (!identity.signedIn) {
    return (
      <Frame
        state="pending"
        heading="You’ve been invited to a team on Diaex."
        body="Create your account — or sign in — and you’ll join your colleagues straight away. You inherit your company’s verification, so you see prices and supplier contacts with no review of your own."
        actions={[
          { label: 'Create my account', onClick: onSignUp, primary: true, testId: 'invite-create-account' },
          { label: 'I already have an account', onClick: onSignIn, testId: 'invite-signin' },
        ]}
        footnote="Not you? You can create a normal account instead — the invite just goes unused."
        testId="invite-signed-out"
      />
    )
  }

  /* ── the result ────────────────────────────────────────────────────── */
  if (outcome.kind === 'joined') {
    const name = outcome.orgName ?? 'your team'
    return (
      <Frame
        state="approved"
        heading={outcome.alreadyMember ? `You’re already on ${name}.` : `You’re in — welcome to ${name}.`}
        body={outcome.alreadyMember
          ? 'Nothing changed; your access is exactly as it was.'
          : 'You inherit this company’s verification, so prices and supplier contacts are open to you now.'}
        actions={[
          { label: 'Open your team', onClick: () => navigate('/account/team'), primary: true, testId: 'invite-team' },
          browse,
        ]}
        announce
        testId="invite-joined"
      >
        {/* Non-blocking + honest: they're in, but with no number on file they
            cannot message our agent until they add and verify one. */}
        {outcome.needsPersonalPhone && (
          <p
            className="mt-4 rounded-md border border-gold/40 bg-gold/5 p-3 text-[13px] leading-relaxed text-ink"
            data-testid="invite-needs-phone"
          >
            <span className="font-semibold">Add your number to message our agent on WhatsApp.</span>{' '}
            You’re in without it — but you can’t message our agent until you add and verify a number, from your profile.
          </p>
        )}
      </Frame>
    )
  }

  if (outcome.kind === 'failed' && outcome.code === 'already_in_org') {
    return (
      <OtherOrg
        conflict={conflict}
        browse={browse}
        leaving={leaving}
        error={leaveError}
        onLeaveAndJoin={() => void leaveAndJoin()}
        onTeam={() => navigate('/account/team')}
      />
    )
  }

  if (outcome.kind === 'failed') {
    return (
      <Failure
        outcome={outcome}
        identityEmail={identity.email}
        browse={browse}
        onSignIn={onSignIn}
        onRetry={() => void accept()}
        onTeam={() => navigate('/account/team')}
      />
    )
  }

  /* ── the confirm ───────────────────────────────────────────────────── */
  return (
    <Frame
      state="pending"
      heading="Join your team on Diaex"
      body={`You’re signed in as ${identity.email ?? 'this account'}. Accepting adds you to the company that invited you — you’ll inherit their verification, so prices and supplier contacts open up straight away.`}
      actions={[
        {
          label: outcome.kind === 'working' ? 'Joining…' : 'Accept the invite',
          onClick: () => void accept(),
          primary: true,
          disabled: outcome.kind === 'working',
          testId: 'invite-accept',
        },
        { label: 'Not now', onClick: () => navigate(exit.path), testId: 'invite-decline' },
      ]}
      footnote="Nothing is charged. Your seat is paid for by the company that invited you. You can come back to this from your account menu."
      testId="invite-confirm"
    >
      {/* Optional personal number — how YOU message our agent on WhatsApp. Not
          required to join; we ask now because the invite link is single-use. */}
      <label className="mt-5 block">
        <span className="text-[13px] font-medium text-ink">Your WhatsApp number <span className="font-normal text-soft">(optional)</span></span>
        <input
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+1 415 555 0100"
          className="input mt-1.5 h-11 w-full text-[16px]"
          data-testid="invite-phone"
        />
        <span className="mt-1 block text-[12px] leading-relaxed text-soft">
          So you can message our agent on WhatsApp. You can add or verify it later from your profile.
        </span>
      </label>
    </Frame>
  )
}

/**
 * `already_in_org`, told by role — because the remedy is different for each and
 * exactly one of them used to be printed for all three.
 *
 * A MEMBER or ADMIN can genuinely leave: `mayRemoveMember` returns ok for
 * `isSelf` on any non-owner row, so the button here does the whole thing —
 * leave, then spend the invite — rather than sending them to hunt for a control
 * on another screen.
 *
 * An OWNER cannot, at all: the owner is never removable and there is no
 * self-serve way out of owning a company. Saying "leave your current team
 * first" to them is an instruction that cannot be followed, which is the defect
 * this branch exists to remove. They get the two things that DO work —
 * transferring ownership on the Team screen, and asking us.
 */
function OtherOrg({
  conflict, browse, leaving, error, onLeaveAndJoin, onTeam,
}: {
  conflict: Conflict
  browse: Action
  leaving: boolean
  error: string | null
  onLeaveAndJoin: () => void
  onTeam: () => void
}) {
  const team = { label: 'Open your team', onClick: onTeam, testId: 'invite-team' }
  const support = {
    label: 'Ask us to move you',
    href: `mailto:${SUPPORT}?subject=${encodeURIComponent('Diaex — moving my account to another company')}`,
    testId: 'invite-support',
  }

  if (conflict.kind === 'loading') {
    return (
      <div className="mx-auto w-full max-w-[560px] px-4 py-10 md:px-6" data-testid="invite-other-org-loading">
        <div className="skeleton h-8 w-64 rounded" />
        <div className="skeleton mt-3 h-4 w-full rounded" />
        <div className="skeleton mt-6 h-11 w-52 rounded-full" />
        <span className="sr-only" role="status">Checking the company you’re in</span>
      </div>
    )
  }

  if (conflict.kind === 'known' && conflict.orgRole === 'owner') {
    return (
      <Frame
        state="rejected"
        heading={`You own ${conflict.orgName}.`}
        body={`On Diaex a person belongs to one company at a time, and an owner can’t simply walk away from theirs — the company would be left with nobody who can manage seats or verification. Hand ${conflict.orgName} to a colleague from your team screen and this invite will work, or tell us and we’ll move your account by hand.`}
        actions={[{ ...team, primary: true }, support, browse]}
        footnote="Your invite stays valid for seven days, so there is no rush."
        testId="invite-other-org-owner"
      />
    )
  }

  if (conflict.kind === 'known') {
    const name = conflict.orgName
    return (
      <Frame
        state="rejected"
        heading={`You’re on ${name} already.`}
        body={`On Diaex a person belongs to one company at a time. Leaving ${name} and joining the company that invited you is one step — you keep your account and everything saved on it, and ${name}’s prices simply stop being yours to see.`}
        actions={[
          {
            label: leaving ? 'Moving you…' : `Leave ${name} and join`,
            onClick: onLeaveAndJoin,
            primary: true,
            disabled: leaving,
            testId: 'invite-leave-and-join',
          },
          { ...team, label: 'Look at your team first' },
          browse,
        ]}
        error={error}
        footnote="Nothing happens until you press it, and your invite stays valid for seven days."
        testId="invite-other-org"
      />
    )
  }

  // The role read failed (or orgs are off). No instruction we cannot stand
  // behind — just the two surfaces that can actually resolve it.
  return (
    <Frame
      state="rejected"
      heading="You’re already part of another company."
      body="On Diaex a person belongs to one company at a time. Your team screen shows which one, and what you can do about it — and if it looks wrong, tell us and we’ll sort it out without you losing anything."
      actions={[{ ...team, primary: true }, support, browse]}
      testId="invite-other-org-unknown"
    />
  )
}

/**
 * Every server refusal, rendered as a sentence with somewhere to go. The code
 * is used to CHOOSE the sentence and is never shown.
 */
function Failure({
  outcome, identityEmail, browse, onSignIn, onRetry, onTeam,
}: {
  outcome: { kind: 'failed'; code: string | null; message: string }
  identityEmail: string | null
  browse: Action
  onSignIn: () => void
  onRetry: () => void
  onTeam: () => void
}) {
  const team = { label: 'Open your team', onClick: onTeam, testId: 'invite-team' }
  const askAgain = {
    label: 'Ask for a new invite',
    href: `mailto:?subject=${encodeURIComponent('Could you re-send my Diaex invite?')}&body=${encodeURIComponent('My Diaex invite link is no longer valid — could you send a fresh one?')}`,
    testId: 'invite-request-new',
  }

  switch (outcome.code) {
    case 'invite_email_mismatch':
      return (
        <Frame
          state="rejected"
          heading="This invite is for a different address."
          // The server names the invited address in its message; repeating
          // BOTH is what turns a refusal into an instruction.
          body={`${outcome.message}${identityEmail ? ` You’re currently signed in as ${identityEmail}.` : ''}`}
          actions={[
            { label: 'Sign in as the invited person', onClick: onSignIn, primary: true, testId: 'invite-signin' },
            browse,
          ]}
          footnote="Signing in as someone else won’t sign you out of anything you still need — you can switch back."
          announce
          testId="invite-wrong-email"
        />
      )
    case 'invite_already_accepted':
      return (
        <Frame
          state="approved"
          heading="That invite has already been used."
          body="If that was you — maybe in another tab — you’re already on the team and nothing is missing."
          actions={[{ ...team, primary: true }, browse]}
          announce
          testId="invite-already-used"
        />
      )
    case 'invite_revoked':
      return (
        <Frame
          state="rejected"
          heading="That invite was revoked."
          body="Whoever invited you cancelled it. That usually means they meant to send a different one."
          actions={[{ ...askAgain, primary: true }, browse]}
          announce
          testId="invite-revoked"
        />
      )
    case 'invite_expired':
      return (
        <Frame
          state="rejected"
          heading="That invite has expired."
          body="Invites last seven days. Asking for a new one takes a moment and everything else stays the same."
          actions={[{ ...askAgain, primary: true }, browse]}
          announce
          testId="invite-expired"
        />
      )
    case 'invite_not_found':
      return (
        <Frame
          state="rejected"
          heading="That invite link is no longer valid."
          body="It may have been used, cancelled, or cut short by an email client. A fresh one will work."
          actions={[{ ...askAgain, primary: true }, browse]}
          announce
          testId="invite-not-found"
        />
      )
    case 'seat_suspended':
      return (
        <Frame
          state="suspended"
          heading="Your seat with this company is paused."
          body="You’re still an employee — an owner or admin has to switch the seat back on. Until then you can browse with prices hidden."
          actions={[{ ...team, primary: true }, browse]}
          announce
          testId="invite-seat-suspended"
        />
      )
    case 'seat_limit_reached':
      return (
        <Frame
          state="pending"
          heading="Your team has run out of seats."
          body="This isn’t anything you did — the company filled its last seat between sending your invite and you opening it. Their admin can add one in a couple of taps, and your invite stays valid."
          actions={[
            {
              label: 'Let them know',
              href: `mailto:?subject=${encodeURIComponent('Diaex — the team is out of seats')}&body=${encodeURIComponent('I tried to accept my Diaex invite but the team has no seats left. Could you add one?')}`,
              primary: true,
              testId: 'invite-notify-admin',
            },
            browse,
          ]}
          announce
          testId="invite-no-seats"
        />
      )
    case 'not_found':
      return (
        <Frame
          state="rejected"
          heading="Team invites aren’t switched on yet."
          body="Nothing is wrong with your account — this part of Diaex just isn’t live here."
          actions={[{ ...browse, primary: true }]}
          announce
          testId="invite-unavailable"
        />
      )
    default:
      return (
        <Frame
          state="rejected"
          heading="We couldn’t use that invite."
          body={outcome.message}
          actions={[
            { label: 'Try again', onClick: onRetry, primary: true, testId: 'invite-retry' },
            browse,
          ]}
          announce
          testId="invite-error"
        />
      )
  }
}

const SUPPORT = 'info@thediaex.com'

interface Action {
  label: string
  onClick?: () => void
  href?: string
  primary?: boolean
  disabled?: boolean
  testId?: string
}

/**
 * One centred card for all fifteen states, so the tone can never drift
 * between "welcome" and "we've refused you" — the same surface says both, and
 * both have somewhere to go.
 */
function Frame({
  state, heading, body, actions, footnote, error, testId, announce = false, children,
}: {
  state: 'approved' | 'pending' | 'rejected' | 'suspended'
  heading: string
  body: string
  actions: Action[]
  footnote?: string
  /** An action that just failed. Rendered IN the card — never a toast, which
   *  would vanish before the sentence explaining it had been read. */
  error?: string | null
  testId: string
  /** Optional extra content (e.g. the personal-phone field) between body and
   *  actions. */
  children?: React.ReactNode
  /**
   * ── THE OUTCOME SWAP WAS SILENT ─────────────────────────────────────────
   * Pressing "Accept the invite" replaces this whole card with a different
   * one. The button the user pressed is unmounted, so focus falls to <body>,
   * and NOTHING is announced — a screen-reader user pressed Accept and was
   * told neither that they had joined a company nor that the invite had been
   * refused. Set on the states that REPLACE another state (never on a first
   * paint, where stealing focus fights the browser's start position): the
   * heading takes focus and is read with its body as its description.
   */
  announce?: boolean
}) {
  const headingRef = useRef<HTMLHeadingElement>(null)
  const bodyId = useId()
  useEffect(() => {
    if (!announce) return
    headingRef.current?.focus()
  }, [announce])

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-10 md:px-6" data-testid={testId}>
      <div className="lqd-glass rounded-lg p-6 md:p-8">
        <StateSeal state={state} size={44} animate />
        <h1
          ref={headingRef}
          tabIndex={announce ? -1 : undefined}
          aria-describedby={announce ? bodyId : undefined}
          className="mt-4 font-serif text-2xl leading-snug tracking-h1 text-ink md:text-3xl focus:outline-none"
        >
          {heading}
        </h1>
        <p id={bodyId} className="mt-3 text-sm leading-relaxed text-soft">{body}</p>
        {error && (
          <p className="mt-3 text-[13px] font-medium text-danger" role="alert" data-testid={`${testId}-error`}>
            {error}
          </p>
        )}
        {children}
        <div className="mt-6 flex flex-wrap gap-2">
          {actions.map((a) => (
            a.href
              ? (
                <a
                  key={a.label}
                  href={a.href}
                  className={`${a.primary ? 'btn-primary' : 'btn-secondary'} inline-flex h-11 items-center justify-center px-5`}
                  data-testid={a.testId}
                >
                  {a.label}
                </a>
              )
              : (
                <PressScale
                  key={a.label}
                  feel="tap"
                  disabled={a.disabled}
                  onClick={a.onClick}
                  className={`${a.primary ? 'btn-primary' : 'btn-secondary'} h-11 px-5`}
                  data-testid={a.testId}
                >
                  {a.label}
                </PressScale>
              )
          ))}
        </div>
        {footnote && <p className="mt-4 text-[12.5px] leading-relaxed text-soft">{footnote}</p>}
      </div>
    </div>
  )
}
